import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  Building2, 
  Users, 
  Target,
  FileCheck, 
  Clock,
  Loader2,
  Calendar,
  Car,
  AlertTriangle,
  CheckCircle,
  Euro
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminDashboard() {
  const [concessionStats, setConcessionStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // KPIs consolidés state
  const [seasonalityData, setSeasonalityData] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Separate state for the table section
  const [tableYear, setTableYear] = useState(new Date().getFullYear().toString());
  const [tableMonth, setTableMonth] = useState(new Date().getMonth() + 1);
  
  const monthNamesFull = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  // Business rule: from summer 2026, non-Fleet July+August merged as "Juillet-Août"
  const SUMMER_MERGE_YEAR = 2026;
  const monthLabelFull = (m, y) => {
    if (m === 7 && y >= SUMMER_MERGE_YEAR) return "Juillet-Août";
    if (m === 8 && y >= SUMMER_MERGE_YEAR) return "Juillet-Août";
    return monthNamesFull[m - 1];
  };
  const monthSelectorItems = (y) => {
    if (y >= SUMMER_MERGE_YEAR) {
      return [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12].map(m => ({ value: m, label: m === 7 ? "Juillet-Août" : monthNamesFull[m - 1] }));
    }
    return monthNamesFull.map((label, idx) => ({ value: idx + 1, label }));
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, tableYear, tableMonth]);

  const fetchData = async () => {
    try {
      const [concessionRes, seasonalityRes, objectivesRes, concessionsRes] = await Promise.all([
        api.get("/statistics/by-concession", { params: { year: parseInt(tableYear), month: tableMonth } }),
        api.get("/seasonality"),
        api.get("/objectives", { params: { year: parseInt(selectedYear) } }),
        api.get("/concessions")
      ]);
      setConcessionStats(concessionRes.data);
      setSeasonalityData(seasonalityRes.data);
      setObjectives(objectivesRes.data);
      setConcessions(concessionsRes.data.filter(c => c.is_active));
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Calcul des KPIs consolidés pour le mois sélectionné
  const getSeasonalityForConcession = (concessionName, month) => {
    return seasonalityData.find(d => d.concession_name === concessionName && d.month === month);
  };

  const getMonthlyBudget = (concessionName, concessionId, month) => {
    const concession = concessions.find(c => c.id === concessionId);
    const seasonality = getSeasonalityForConcession(concessionName, month);
    if (!concession || !seasonality) return { vn: 0, vo: 0, dfs: 0 };
    return {
      vn: Math.round((concession.budget_vn || 0) * (seasonality.vn_percent || 0) / 100),
      vo: Math.round((concession.budget_vo || 0) * (seasonality.vo_percent || 0) / 100),
      dfs: seasonality.dfs_budget || Math.round((concession.budget_dfs || 0) / 11)
    };
  };

  const getObjectivesForConcession = (concessionId, month) => {
    const concession = concessions.find(c => c.id === concessionId);
    // Détecter Fleet par le flag is_fleet ou le nom
    const isFleet = concession && (concession.is_fleet === true || concession.objective_type === 'quarterly' || concession.name?.toUpperCase() === 'FLEET');
    
    if (isFleet) {
      // Pour FLEET, les objectifs sont trimestriels
      // On récupère l'objectif du trimestre et on divise par 3
      const quarter = Math.ceil(month / 3);
      const quarterMonths = {
        1: [1, 2, 3],
        2: [4, 5, 6],
        3: [7, 8, 9],
        4: [10, 11, 12]
      };
      
      // Récupérer les objectifs du trimestre (stockés sur le premier mois du trimestre ou sur chaque mois)
      const quarterObjs = objectives.filter(o => 
        o.concession_id === concessionId && 
        quarterMonths[quarter].includes(o.month)
      );
      
      const quarterTotal = {
        vn: quarterObjs.reduce((sum, o) => sum + (o.vn_target || 0), 0),
        vo: quarterObjs.reduce((sum, o) => sum + (o.vo_target || 0), 0),
        dfs: quarterObjs.reduce((sum, o) => sum + (o.financing_target || 0), 0)
      };
      
      // Diviser par 3 pour avoir une estimation mensuelle
      return {
        vn: Math.round(quarterTotal.vn / 3),
        vo: Math.round(quarterTotal.vo / 3),
        dfs: Math.round(quarterTotal.dfs / 3),
        isFleet: true
      };
    }
    
    // Pour les autres concessions, calcul mensuel normal
    const objs = objectives.filter(o => o.concession_id === concessionId && o.month === month);
    return {
      vn: objs.reduce((sum, o) => sum + (o.vn_target || 0), 0),
      vo: objs.reduce((sum, o) => sum + (o.vo_target || 0), 0),
      dfs: objs.reduce((sum, o) => sum + (o.financing_target || 0), 0),
      isFleet: false
    };
  };

  // Filter out Fleet concessions from consolidated view
  const nonFleetConcessions = concessions.filter(c => 
    !(c.is_fleet === true || c.objective_type === 'quarterly' || c.name?.toUpperCase() === 'FLEET')
  );

  const calculateMonthlyKPIs = () => {
    let totalBudgetVN = 0, totalObjectiveVN = 0;
    let totalBudgetVO = 0, totalObjectiveVO = 0;
    let totalBudgetDFS = 0, totalObjectiveDFS = 0;
    
    // Summer merged (non-Fleet): if selected month is 7 or 8 in >=2026, aggregate both
    const yearInt = parseInt(selectedYear);
    const isSummerMerged = yearInt >= SUMMER_MERGE_YEAR && (selectedMonth === 7 || selectedMonth === 8);
    const monthsToAgg = isSummerMerged ? [7, 8] : [selectedMonth];
    
    nonFleetConcessions.forEach(concession => {
      monthsToAgg.forEach(m => {
        const monthlyBudget = getMonthlyBudget(concession.name, concession.id, m);
        const objectiveData = getObjectivesForConcession(concession.id, m);
        totalBudgetVN += monthlyBudget.vn;
        totalObjectiveVN += objectiveData.vn;
        totalBudgetVO += monthlyBudget.vo;
        totalObjectiveVO += objectiveData.vo;
        totalBudgetDFS += monthlyBudget.dfs;
        totalObjectiveDFS += objectiveData.dfs;
      });
    });
    
    const percentVN = totalBudgetVN > 0 ? Math.round((totalObjectiveVN / totalBudgetVN) * 100) : 0;
    const percentVO = totalBudgetVO > 0 ? Math.round((totalObjectiveVO / totalBudgetVO) * 100) : 0;
    const percentDFS = totalBudgetDFS > 0 ? Math.round((totalObjectiveDFS / totalBudgetDFS) * 100) : 0;
    
    return {
      vn: { budget: totalBudgetVN, objective: totalObjectiveVN, ecart: totalObjectiveVN - totalBudgetVN, percent: percentVN },
      vo: { budget: totalBudgetVO, objective: totalObjectiveVO, ecart: totalObjectiveVO - totalBudgetVO, percent: percentVO },
      dfs: { budget: totalBudgetDFS, objective: totalObjectiveDFS, ecart: totalObjectiveDFS - totalBudgetDFS, percent: percentDFS }
    };
  };

  const getStatusColor = (percent) => {
    if (percent >= 100) return { bg: "bg-green-500", text: "text-green-700", light: "bg-green-100" };
    if (percent >= 90) return { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-100" };
    return { bg: "bg-red-500", text: "text-red-700", light: "bg-red-100" };
  };

  const monthlyKPIs = calculateMonthlyKPIs();
  const vnStatus = getStatusColor(monthlyKPIs.vn.percent);
  const voStatus = getStatusColor(monthlyKPIs.vo.percent);
  const dfsStatus = getStatusColor(monthlyKPIs.dfs.percent);

  // Calculate totals for the table (excluding Fleet)
  const nonFleetStats = concessionStats.filter(c => {
    const conc = concessions.find(cc => cc.name === c.name || cc.id === c.id);
    return !(conc && (conc.is_fleet === true || conc.objective_type === 'quarterly' || conc.name?.toUpperCase() === 'FLEET'));
  });

  const totals = nonFleetStats.reduce((acc, c) => ({
    sellers: acc.sellers + c.sellers_count,
    pending: acc.pending + c.objectives_pending,
    signed: acc.signed + c.objectives_signed
  }), { sellers: 0, pending: 0, signed: 0 });

  if (loading) {
    return (
      <Layout title="Tableau de bord">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Tableau de bord administrateur">
      {/* ========== VISION CONSOLIDÉE DU MOIS ========== */}
      <div className="bg-gradient-to-br from-[#212E38] to-[#1a252e] rounded-2xl p-6 text-white mb-8" data-testid="monthly-kpis-section">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-3 rounded-xl">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Vision consolidée - {monthLabelFull(selectedMonth, parseInt(selectedYear))} {selectedYear}
              </h2>
              <p className="text-gray-400 text-sm">Comparaison objectifs vs saisonnalité historique</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-24 bg-white/10 border-white/20 text-white" data-testid="kpi-year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2027">2027</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-36 bg-white/10 border-white/20 text-white" data-testid="month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthSelectorItems(parseInt(selectedYear)).map(({ value, label }) => (
                  <SelectItem key={value} value={value.toString()}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* KPIs Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* VN Card */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10 flex flex-col" data-testid="kpi-vn">
            <div className="flex items-start justify-between mb-3 h-[52px]">
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <span className="font-semibold leading-tight">Véhicules Neufs<br/>(VN)</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-bold flex-shrink-0 ${vnStatus.light} ${vnStatus.text}`}>
                {monthlyKPIs.vn.percent}%
              </span>
            </div>
            
            <div className="space-y-3 flex-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Objectifs fixés</span>
                <span className="text-2xl font-bold">{monthlyKPIs.vn.objective}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Budget saisonnalité</span>
                <span className="text-lg text-gray-300">{Math.round(monthlyKPIs.vn.budget)}</span>
              </div>
              
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${vnStatus.bg} transition-all duration-500`}
                  style={{ width: `${Math.min(monthlyKPIs.vn.percent, 100)}%` }}
                />
              </div>
              
              <div className={`text-center py-2 rounded-lg ${monthlyKPIs.vn.ecart >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <span className={`font-bold ${monthlyKPIs.vn.ecart >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  Écart: {monthlyKPIs.vn.ecart >= 0 ? '+' : ''}{monthlyKPIs.vn.ecart} véhicules
                </span>
              </div>
            </div>
          </div>
          
          {/* VO Card */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10 flex flex-col" data-testid="kpi-vo">
            <div className="flex items-start justify-between mb-3 h-[52px]">
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                <span className="font-semibold leading-tight">Véhicules Occasion<br/>(VO)</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-bold flex-shrink-0 ${voStatus.light} ${voStatus.text}`}>
                {monthlyKPIs.vo.percent}%
              </span>
            </div>
            
            <div className="space-y-3 flex-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Objectifs fixés</span>
                <span className="text-2xl font-bold">{monthlyKPIs.vo.objective}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Budget saisonnalité</span>
                <span className="text-lg text-gray-300">{Math.round(monthlyKPIs.vo.budget)}</span>
              </div>
              
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${voStatus.bg} transition-all duration-500`}
                  style={{ width: `${Math.min(monthlyKPIs.vo.percent, 100)}%` }}
                />
              </div>
              
              <div className={`text-center py-2 rounded-lg ${monthlyKPIs.vo.ecart >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <span className={`font-bold ${monthlyKPIs.vo.ecart >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  Écart: {monthlyKPIs.vo.ecart >= 0 ? '+' : ''}{monthlyKPIs.vo.ecart} véhicules
                </span>
              </div>
            </div>
          </div>
          
          {/* DFS Card */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10 flex flex-col" data-testid="kpi-dfs">
            <div className="flex items-start justify-between mb-3 h-[52px]">
              <div className="flex items-center gap-2">
                <Euro className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <span className="font-semibold leading-tight">Financement<br/>(DFS)</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-bold flex-shrink-0 ${dfsStatus.light} ${dfsStatus.text}`}>
                {monthlyKPIs.dfs.percent}%
              </span>
            </div>
            
            <div className="space-y-3 flex-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Objectifs fixés</span>
                <span className="text-2xl font-bold">{monthlyKPIs.dfs.objective.toLocaleString('fr-FR')} €</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400 text-sm">Budget saisonnalité</span>
                <span className="text-lg text-gray-300">{Math.round(monthlyKPIs.dfs.budget).toLocaleString('fr-FR')} €</span>
              </div>
              
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${dfsStatus.bg} transition-all duration-500`}
                  style={{ width: `${Math.min(monthlyKPIs.dfs.percent, 100)}%` }}
                />
              </div>
              
              <div className={`text-center py-2 rounded-lg ${monthlyKPIs.dfs.ecart >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <span className={`font-bold ${monthlyKPIs.dfs.ecart >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  Écart: {monthlyKPIs.dfs.ecart >= 0 ? '+' : ''}{monthlyKPIs.dfs.ecart.toLocaleString('fr-FR')} €
                </span>
              </div>
            </div>
          </div>
          
          {/* Global Status Card */}
          <div className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10 flex flex-col" data-testid="kpi-global">
            <div className="flex items-center gap-2 mb-3 min-h-[40px]">
              <Target className="w-5 h-5 text-amber-400" />
              <span className="font-semibold">Statut Global</span>
            </div>
            
            <div className="space-y-4 flex-1">
              <div className="text-center">
                {(monthlyKPIs.vn.percent >= 100 && monthlyKPIs.vo.percent >= 100 && monthlyKPIs.dfs.percent >= 100) ? (
                  <div className="flex flex-col items-center gap-2">
                    <CheckCircle className="w-12 h-12 text-green-400" />
                    <span className="text-green-400 font-bold text-lg">Objectifs alignés</span>
                    <span className="text-gray-400 text-sm">VN, VO et DFS couvrent la saisonnalité</span>
                  </div>
                ) : (monthlyKPIs.vn.percent >= 90 && monthlyKPIs.vo.percent >= 90) ? (
                  <div className="flex flex-col items-center gap-2">
                    <AlertTriangle className="w-12 h-12 text-amber-400" />
                    <span className="text-amber-400 font-bold text-lg">Attention requise</span>
                    <span className="text-gray-400 text-sm">Objectifs proches du budget (90-99%)</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <AlertTriangle className="w-12 h-12 text-red-400" />
                    <span className="text-red-400 font-bold text-lg">Écart significatif</span>
                    <span className="text-gray-400 text-sm">Objectifs inférieurs à la saisonnalité</span>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
                <div className="text-center">
                  <div className="text-xs text-gray-400">VN</div>
                  <div className={`font-bold ${monthlyKPIs.vn.percent >= 100 ? 'text-green-400' : 'text-red-400'}`}>
                    {monthlyKPIs.vn.percent}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-400">VO</div>
                  <div className={`font-bold ${monthlyKPIs.vo.percent >= 100 ? 'text-green-400' : 'text-red-400'}`}>
                    {monthlyKPIs.vo.percent}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-400">DFS</div>
                  <div className={`font-bold ${monthlyKPIs.dfs.percent >= 100 ? 'text-green-400' : 'text-red-400'}`}>
                    {monthlyKPIs.dfs.percent}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Simplified Concessions Table */}
      <div className="card-dashboard" data-testid="concessions-stats-table">
        <div className="flex items-center justify-between mb-4">
          <h2 
            className="text-lg font-bold text-[#212E38]"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            État des affectations d'objectifs par concessions
          </h2>
          
          <div className="flex items-center gap-2">
            <Select value={tableYear} onValueChange={setTableYear}>
              <SelectTrigger className="w-24 h-9" data-testid="table-year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2027">2027</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={tableMonth.toString()} onValueChange={(v) => setTableMonth(parseInt(v))}>
              <SelectTrigger className="w-32 h-9" data-testid="table-month-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthSelectorItems(parseInt(tableYear)).map(({ value, label }) => (
                  <SelectItem key={value} value={value.toString()}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {nonFleetStats.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Concession</TableHead>
                <TableHead className="text-center font-semibold">Nombre de vendeurs</TableHead>
                <TableHead className="text-center font-semibold">En attente</TableHead>
                <TableHead className="text-center font-semibold">Signé</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nonFleetStats.map((c) => (
                <TableRow key={c.id} data-testid={`concession-row-${c.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      {c.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{c.sellers_count}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {c.objectives_pending > 0 ? (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        <Clock className="w-3 h-3 mr-1" />
                        {c.objectives_pending}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {c.objectives_signed > 0 ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <FileCheck className="w-3 h-3 mr-1" />
                        {c.objectives_signed}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              
              {/* Total Row */}
              <TableRow className="bg-gray-100 font-semibold" data-testid="totals-row">
                <TableCell className="font-bold">
                  TOTAL
                </TableCell>
                <TableCell className="text-center font-bold">{totals.sellers}</TableCell>
                <TableCell className="text-center">
                  {totals.pending > 0 ? (
                    <Badge className="bg-amber-500 text-white">
                      {totals.pending}
                    </Badge>
                  ) : (
                    <span className="text-gray-500">0</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {totals.signed > 0 ? (
                    <Badge className="bg-green-500 text-white">
                      {totals.signed}
                    </Badge>
                  ) : (
                    <span className="text-gray-500">0</span>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <div className="empty-state">
            <Building2 className="empty-state-icon" />
            <p>Aucune concession trouvée</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
